import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, ShieldOff, ArrowLeft, Mail, RefreshCw, UserPlus,
  Search, UserX, Trash2, CheckCircle2, AlertTriangle, Users
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

export default function AdminNewsletter() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '' });
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [actioning, setActioning] = useState(null); // email currently being unsubscribed/deleted
  const queryClient = useQueryClient();

  const { data: mc = {}, isLoading, refetch } = useQuery({
    queryKey: ['admin-newsletter-members'],
    queryFn: async () => {
      const res = await base44.functions.invoke('adminManageNewsletter', { action: 'list_members' });
      return res.data;
    },
    enabled: currentUser?.role === 'admin',
  });

  const members = mc.members || [];

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      await refetch();
      setSyncMsg({ success: true, text: `Synced ${members.length || mc.total_items || 0} subscribers from Mailchimp.` });
    } catch (e) {
      setSyncMsg({ success: false, text: 'Sync failed.' });
    }
    setSyncing(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddResult(null);
    try {
      const res = await base44.functions.invoke('adminManageNewsletter', {
        action: 'add',
        email: addForm.email,
        first_name: addForm.first_name,
        last_name: addForm.last_name,
      });
      if (res.data?.error) {
        setAddResult({ success: false, message: res.data.error });
      } else {
        setAddResult({ success: true, message: `${addForm.email} added to Mailchimp.` });
        setAddForm({ email: '', first_name: '', last_name: '' });
        refetch();
      }
    } catch (err) {
      setAddResult({ success: false, message: 'Failed to add subscriber.' });
    }
    setAdding(false);
  };

  const runAction = async () => {
    if (!actionTarget) return;
    const { email, action } = actionTarget;
    setActioning(email);
    try {
      const res = await base44.functions.invoke('adminManageNewsletter', { action, email });
      if (res.data?.error) {
        toast({ title: res.data.error, variant: 'destructive' });
      } else {
        toast({ title: action === 'delete' ? 'Subscriber deleted' : 'Subscriber unsubscribed' });
        refetch();
        setActionTarget(null);
      }
    } catch (e) {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
    }
    setActioning(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    const name = `${m.first_name} ${m.last_name}`.trim().toLowerCase();
    const matchQ = !q || m.email.toLowerCase().includes(q) || name.includes(q);
    const matchS = statusFilter === 'all' || m.status === statusFilter;
    return matchQ && matchS;
  });

  const subscribedCount = members.filter((m) => m.status === 'subscribed').length;
  const unsubscribedCount = members.filter((m) => m.status === 'unsubscribed').length;

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
              <h1 className="text-white font-black text-3xl md:text-4xl uppercase">NEWSLETTER</h1>
              <p className="text-white/30 text-xs mt-2">
                Synced with Mailchimp audience: <span className="text-white font-medium">{mc.list_name || '—'}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSync}
                disabled={syncing || isLoading}
                className="flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-all disabled:opacity-40"
              >
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                SYNC FROM MAILCHIMP
              </button>
              <button
                onClick={() => setAddOpen((o) => !o)}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-colors"
              >
                <UserPlus className="w-4 h-4" /> ADD SUBSCRIBER
              </button>
            </div>
          </div>

          {syncMsg && (
            <div
              className={`mt-4 flex items-center gap-2 px-4 py-2.5 text-xs font-medium border ${
                syncMsg.success
                  ? 'border-green-500/20 bg-green-500/5 text-green-400'
                  : 'border-red-500/20 bg-red-500/5 text-red-400'
              }`}
            >
              {syncMsg.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {syncMsg.text}
            </div>
          )}

          {/* Add subscriber form */}
          {addOpen && (
            <form onSubmit={handleAdd} className="mt-4 border border-primary/20 bg-primary/5 p-4 space-y-4">
              <p className="text-primary text-[10px] font-bold tracking-widest">ADD SUBSCRIBER TO MAILCHIMP</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">FIRST NAME</p>
                  <Input
                    value={addForm.first_name}
                    onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))}
                    placeholder="John"
                    className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60"
                  />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">LAST NAME</p>
                  <Input
                    value={addForm.last_name}
                    onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))}
                    placeholder="Smith"
                    className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60"
                  />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">EMAIL *</p>
                  <Input
                    type="email"
                    required
                    value={addForm.email}
                    onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={adding || !addForm.email}
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-colors"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {adding ? 'ADDING...' : 'ADD TO MAILCHIMP'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setAddResult(null); }} className="text-white/40 hover:text-white text-xs font-bold tracking-wider transition-colors">
                  CANCEL
                </button>
              </div>
              {addResult && (
                <div className={`flex items-center gap-2 px-3 py-2 text-xs border ${addResult.success ? 'bg-green-500/5 border-green-500/20 text-green-400' : 'bg-red-500/5 border-red-500/20 text-red-400'}`}>
                  {addResult.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {addResult.message}
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 lg:px-10 pt-10 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="border border-white/5 bg-[#111115] p-5">
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TOTAL SUBSCRIBERS</p>
            <p className="text-white font-black text-3xl">{mc.total_items ?? members.length}</p>
          </div>
          <div className="border border-green-500/10 bg-green-500/5 p-5">
            <p className="text-green-400/60 text-[10px] font-bold tracking-widest mb-1">SUBSCRIBED</p>
            <p className="text-green-400 font-black text-3xl">{subscribedCount}</p>
          </div>
          <div className="border border-white/5 bg-[#111115] p-5">
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">UNSUBSCRIBED</p>
            <p className="text-white/40 font-black text-3xl">{unsubscribedCount}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <div className="flex gap-1 border border-white/10 bg-[#111115] h-11">
            {['all', 'subscribed', 'unsubscribed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 text-[10px] font-bold tracking-widest transition-colors ${
                  statusFilter === s ? 'bg-primary/10 text-primary' : 'text-white/40 hover:text-white'
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Mail className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No subscribers found</p>
          </div>
        ) : (
          <div className="border border-white/5 overflow-x-auto">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5 min-w-[600px]">
              <p className="col-span-5 text-white/30 text-[10px] font-bold tracking-widest">EMAIL</p>
              <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">NAME</p>
              <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">STATUS</p>
              <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest text-right">ACTIONS</p>
            </div>
            {filtered.map((m) => {
              const name = `${m.first_name} ${m.last_name}`.trim();
              return (
                <div key={m.email} className="grid grid-cols-12 gap-4 px-6 py-3.5 border-b border-white/5 hover:bg-white/[0.02] transition-colors items-center min-w-[600px]">
                  <p className="col-span-5 text-white text-sm truncate">{m.email}</p>
                  <p className="col-span-3 text-white/50 text-sm truncate">{name || '—'}</p>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center text-[10px] font-bold tracking-wider px-2 py-0.5 ${m.status === 'subscribed' ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/30'}`}>
                      {m.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    {m.status === 'subscribed' && (
                      <button
                        onClick={() => setActionTarget({ email: m.email, action: 'unsubscribe', name: m.email })}
                        disabled={actioning === m.email}
                        title="Unsubscribe"
                        className="p-1.5 border border-white/10 text-white/30 hover:border-yellow-400/40 hover:text-yellow-400 transition-all disabled:opacity-40"
                      >
                        {actioning === m.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => setActionTarget({ email: m.email, action: 'delete', name: m.email })}
                      disabled={actioning === m.email}
                      title="Permanently delete"
                      className="p-1.5 border border-white/10 text-white/30 hover:border-red-500/40 hover:text-red-400 transition-all disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <ConfirmDialog
        open={!!actionTarget}
        title={actionTarget?.action === 'delete' ? 'Delete subscriber' : 'Unsubscribe subscriber'}
        description={actionTarget ? `${actionTarget.action === 'delete' ? 'Permanently delete' : 'Unsubscribe'} ${actionTarget.email} from Mailchimp?` : ''}
        confirmLabel={actionTarget?.action === 'delete' ? 'Delete' : 'Unsubscribe'}
        busy={!!actioning}
        onConfirm={runAction}
        onClose={() => setActionTarget(null)}
      />
    </div>
  );
}